import type { TeamSpace } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  ErrorBoundary,
  FormTextArea,
  RadioBigCard,
  RadioGroup,
  Separator,
  Skeleton,
  Text,
  type ThemeKeys,
} from "@prismicio/editor-ui"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { Suspense, useState } from "react"

import { LocaleSelection } from "../../../app/components/locales/LocaleSelection"
import {
  useAiTranslationFlag,
  useGlossaryFlag,
} from "../../../app/integrations/AmplitudeExperiments"
import { getRepositoryName } from "../../../app/routing"
import type { Locale } from "../../../app/services/locales"
import { createDocumentPermission } from "../../../app/services/permissions"
import { useRepositoryV2 } from "../../../app/services/repository"
import { useMyRole } from "../../../app/services/role"
import { getAiTranslationFeedbackOptions } from "../../../app/services/translateDocument"
import { useDocument } from "../../DocumentProvider"
import { AiTranslationFeedbackDialog } from "./AiTranslationFeedbackDialog"
import { GlossaryDialog } from "./GlossaryDialog"
import { GlossarySection } from "./GlossarySection"
import { useLocales } from "./useLocales"

export function Locales() {
  const { type, teamSpace } = useDocument()

  const {
    onLocaleChange,
    isEnabled,
    locale,
    langs: locales,
    onCopyToLocale,
    onTranslateToLocale,
    cancelTranslation,
    error,
    clearError,
  } = useLocales()

  const [dialogOpen, setDialogOpen] = useState(false)

  function onOpenChange(open: boolean) {
    if (!open) setDialogOpen(open)
  }

  useOnChange(dialogOpen, (open) => {
    if (!open) cancelTranslation()
  })

  if (!locale) return null

  if (locales.length < 2) return null

  return (
    <Box flexShrink={0} flexDirection="column">
      <Box
        padding={{ bottom: 6 }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="smallBold" color="grey9" align="end">
          Locales
        </Text>
        <Button
          invisible
          size="small"
          onClick={() => setDialogOpen(true)}
          disabled={type === "new"}
          underline
        >
          Copy to another locale
        </Button>
      </Box>
      <CopyToLocaleDialog
        open={dialogOpen}
        onOpenChange={onOpenChange}
        locale={locale}
        teamSpace={teamSpace}
        locales={locales}
        isEnabled={isEnabled}
        onCopyToLocale={onCopyToLocale}
        onTranslateToLocale={onTranslateToLocale}
        error={error}
        clearError={clearError}
      />
      <LocaleSelection
        locale={{ id: locale, customName: null }}
        isEnabled={isEnabled}
        onLocaleChange={({ id }) => onLocaleChange(id)}
        locales={locales}
      />

      <Separator sx={{ marginBlock: 24 }} />
    </Box>
  )
}

interface CopyToLocaleProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locale: string
  teamSpace: TeamSpace
  onCopyToLocale: (localeId: string) => void
  onTranslateToLocale: (
    localeId: string,
    additionalInstructions: string,
  ) => void
  locales: Locale[]
  isEnabled: boolean
  error?: { title: string; description: string }
  clearError: () => void
}

function CopyToLocaleDialog(props: CopyToLocaleProps) {
  const {
    open,
    onOpenChange,
    locale,
    teamSpace,
    onCopyToLocale,
    onTranslateToLocale,
    locales,
    isEnabled,
    error,
    clearError,
  } = props

  const [mode, setMode] = useState<CopyToLocaleMode>(copyToLocaleModes[0])

  const localesWithoutSourceLocale = locales.filter((l) => l.id !== locale)
  const defaultTargetLocaleId = localesWithoutSourceLocale[0]?.id || ""

  const [targetLocaleId, setTargetLocaleId] = useState(defaultTargetLocaleId)

  const [additionalInstructions, setAdditionalInstructions] = useState("")
  const additionalInstructionsError =
    additionalInstructions.length > 1000
      ? "Must be equal to or less than 1000 characters long"
      : undefined

  const formError = mode === "translate" && Boolean(additionalInstructionsError)

  useOnChange(open, (open) => {
    if (!open) {
      setMode(copyToLocaleModes[0])
      setTargetLocaleId(defaultTargetLocaleId)
    }
  })

  function onModeChange(mode: CopyToLocaleMode) {
    setMode(mode)
    clearError()
  }

  function onLocaleChange(localeId: string) {
    setTargetLocaleId(localeId)
    clearError()
  }

  const aiTranslationFlag = useAiTranslationFlag({ active: open })
  const glossaryEnabled = useGlossaryFlag() === "on"

  const trialTranslationEnabled = aiTranslationFlag === "trial"
  const unlimitedTranslationEnabled = aiTranslationFlag === "unlimited"
  const translationsEnabled =
    trialTranslationEnabled || unlimitedTranslationEnabled

  const okButtonTitle = okButtonTitles[mode]

  const role = useMyRole({
    localeId: targetLocaleId,
    teamSpaceId: teamSpace.id,
  })
  const canCreateDocument = createDocumentPermission[role]

  const { data: repository } = useRepositoryV2()
  const aiTranslationsLeft = repository?.quotas.aiTranslationsLeft || 0
  const noTranslationsLeft =
    mode === "translate" && trialTranslationEnabled && aiTranslationsLeft === 0

  const okButtonDisabled = !canCreateDocument || noTranslationsLeft || formError

  function getOkButtonTooltipLabel() {
    if (!canCreateDocument)
      return "You don't have permission to create documents in this locale"
    if (noTranslationsLeft) return "You don't have any translations left"
    if (formError) return "There are errors in the form"
    return undefined
  }

  function okButtonOnClick(localeId: string) {
    switch (mode) {
      case "copy":
        return onCopyToLocale(localeId)
      case "translate":
        return onTranslateToLocale(localeId, additionalInstructions.trim())
    }
  }

  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)

  const [isGlossaryDialogOpen, setIsGlossaryDialogOpen] = useState(false)

  const isCopyToLocaleDialogOpen =
    open && !isFeedbackDialogOpen && !isGlossaryDialogOpen

  return (
    <>
      <Dialog
        open={isCopyToLocaleDialogOpen}
        onOpenChange={onOpenChange}
        size={{ height: "auto", width: 656 }}
        noDescription
      >
        <DialogHeader icon="language" title="Copy to another locale" />
        <DialogContent>
          <Box
            overflowY="auto"
            padding={{ inline: 16, top: 16, bottom: 8 }}
            flexDirection="column"
            gap={16}
          >
            {error && (
              <Alert
                icon="alert"
                color="error"
                noShadow
                title={error.title}
                subtitle={error.description}
                footerButtons={{
                  action: {
                    text: "Try again",
                    onClick: () => {
                      okButtonOnClick(targetLocaleId)
                    },
                  },
                }}
              />
            )}
            {translationsEnabled && (
              <>
                <RadioGroup
                  disabled={!isEnabled}
                  value={mode}
                  onChange={onModeChange}
                >
                  <CopyCard />
                  <TranslateCard
                    aiTranslationFlag={aiTranslationFlag}
                    setIsFeedbackDialogOpen={setIsFeedbackDialogOpen}
                  />
                </RadioGroup>

                <Separator style="dashed" />
              </>
            )}
            <Box flexDirection="column" gap={4}>
              <Text variant="emphasized" color="grey12">
                Target language
              </Text>
              <LocaleSelection
                isEnabled={isEnabled && !noTranslationsLeft}
                locale={{ id: targetLocaleId, customName: null }}
                onLocaleChange={({ id }) => onLocaleChange(id)}
                locales={localesWithoutSourceLocale}
              />
            </Box>

            {mode === "translate" && (
              <>
                {glossaryEnabled && (
                  <GlossarySection
                    sourceLocaleId={locale}
                    targetLocaleId={targetLocaleId}
                    onOpenGlossaryDialog={() => setIsGlossaryDialogOpen(true)}
                  />
                )}
                <FormTextArea
                  label="Additional instructions (optional)"
                  placeholder="Add tone of voice, preferred terminology, or translation notes for better results"
                  name="additionalInstructions"
                  value={additionalInstructions}
                  onChange={setAdditionalInstructions}
                  error={additionalInstructionsError}
                  readOnly={!isEnabled || noTranslationsLeft}
                />
              </>
            )}
          </Box>

          <DialogActions>
            <DialogCancelButton />
            <DialogActionButton
              onClick={() => okButtonOnClick(targetLocaleId)}
              loading={!isEnabled}
              disabled={okButtonDisabled}
              tooltipLabel={getOkButtonTooltipLabel()}
            >
              {okButtonTitle}
            </DialogActionButton>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {open && trialTranslationEnabled && (
        <ErrorBoundary>
          <Suspense>
            <AiTranslationFeedbackDialog
              open={isFeedbackDialogOpen}
              onOpenChange={setIsFeedbackDialogOpen}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {open && mode === "translate" && glossaryEnabled && (
        <ErrorBoundary>
          <Suspense>
            <GlossaryDialog
              open={isGlossaryDialogOpen}
              onOpenChange={setIsGlossaryDialogOpen}
              sourceLocaleId={locale}
              targetLocaleId={targetLocaleId}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  )
}

const copyToLocaleModes = ["copy", "translate"] as const

type CopyToLocaleMode = (typeof copyToLocaleModes)[number]

const okButtonTitles = {
  copy: "Copy all content",
  translate: "Translate content",
}

function CopyCard() {
  return (
    <RadioBigCard
      value="copy"
      icon="contentCopy"
      title="Copy all content"
      description="Create a new locale version by copying all the content from this page. Ideal for manual translation and adjustments."
      grow
    />
  )
}

interface TranslateCardProps {
  aiTranslationFlag: "trial" | "unlimited"
  setIsFeedbackDialogOpen: (open: boolean) => void
}

function TranslateCard(props: TranslateCardProps) {
  const { aiTranslationFlag, setIsFeedbackDialogOpen } = props

  if (aiTranslationFlag === "unlimited") {
    return (
      <RadioBigCard
        value="translate"
        icon="translate"
        title="Translate with AI"
        description="Automatically copy and translate the content of this page into the selected language."
        grow
      />
    )
  }

  return (
    <TrialTranslateCard setIsFeedbackDialogOpen={setIsFeedbackDialogOpen} />
  )
}

interface TrialTranslateCardProps {
  setIsFeedbackDialogOpen: (open: boolean) => void
}

function TrialTranslateCard(props: TrialTranslateCardProps) {
  const { setIsFeedbackDialogOpen } = props
  const { data: repository } = useRepositoryV2()
  const aiTranslationsLeft = repository?.quotas.aiTranslationsLeft || 0

  const { data: aiTranslationFeedback } = useQuery(
    getAiTranslationFeedbackOptions(),
  )

  const repositorySubmitted =
    aiTranslationFeedback?.repositorySubmitted || false

  return (
    <RadioBigCard
      value="translate"
      icon="translate"
      title="Translate with AI"
      description={
        aiTranslationsLeft > 3 ||
        (aiTranslationsLeft > 0 &&
          aiTranslationsLeft <= 3 &&
          repositorySubmitted)
          ? "Automatically copy and translate the content of this page into the selected language."
          : undefined
      }
      badge={{
        title: `Free translations left: ${aiTranslationsLeft}`,
      }}
      footer={
        aiTranslationsLeft <= 3 && (
          <ErrorBoundary>
            <Suspense fallback={<Skeleton height={24} width={200} />}>
              <TrialTranslateCardFooter
                setIsFeedbackDialogOpen={setIsFeedbackDialogOpen}
                aiTranslationsLeft={aiTranslationsLeft}
              />
            </Suspense>
          </ErrorBoundary>
        )
      }
      grow
    />
  )
}

interface TrialTranslateCardFooterProps {
  setIsFeedbackDialogOpen: (open: boolean) => void
  aiTranslationsLeft: number
}

function TrialTranslateCardFooter(props: TrialTranslateCardFooterProps) {
  const { setIsFeedbackDialogOpen, aiTranslationsLeft } = props

  const { data: aiTranslationFeedback } = useSuspenseQuery(
    getAiTranslationFeedbackOptions(),
  )
  const { repositorySubmitted, userSubmitted } = aiTranslationFeedback

  if (!repositorySubmitted && !userSubmitted) {
    return (
      <>
        <SpanButton onClick={() => setIsFeedbackDialogOpen(true)}>
          <Text
            variant="bold"
            color="purple11"
            align="center"
            underline
            component="span"
          >
            Get 5 more for free!
          </Text>
        </SpanButton>

        <Text color="grey11" align="center" sx={{ marginTop: 6 }}>
          By sharing your feedback with us, your repository will receive 5
          additional translations for free.
        </Text>
      </>
    )
  }

  if (repositorySubmitted && !userSubmitted) {
    return (
      <>
        <SpanButton onClick={() => setIsFeedbackDialogOpen(true)}>
          <Text
            variant="bold"
            color="purple11"
            align="center"
            underline
            component="span"
          >
            Help us improve Translate with AI!
          </Text>
        </SpanButton>

        <ContactSalesMessage
          aiTranslationsLeft={aiTranslationsLeft}
          marginTop={6}
        />
      </>
    )
  }

  return <ContactSalesMessage aiTranslationsLeft={aiTranslationsLeft} />
}

interface SpanButtonProps {
  children: React.ReactNode
  onClick: () => void
}

function SpanButton(props: SpanButtonProps) {
  const { children, onClick } = props

  function onKeyDown(e: React.KeyboardEvent<HTMLSpanElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{ cursor: "pointer" }}
    >
      {children}
    </span>
  )
}

const aiTranslationsContactSalesUrl = `https://share.hsforms.com/1tk4APT30SY-VZU42zyNr3w37uwq?repository_name=${getRepositoryName()}`

interface ContactSalesMessageProps {
  aiTranslationsLeft: number
  marginTop?: ThemeKeys<"space">
}

function ContactSalesMessage(props: ContactSalesMessageProps) {
  const { aiTranslationsLeft, marginTop } = props

  if (aiTranslationsLeft > 0) return null

  return (
    <>
      <Text color="grey11" align="center" sx={{ marginTop }}>
        You've used up all of your free AI translations.
      </Text>
      <Text
        color="purple11"
        align="center"
        underline
        component="a"
        href={aiTranslationsContactSalesUrl}
        target="_blank"
        inline
      >
        Contact Sales to get more.
      </Text>
    </>
  )
}
